'use client';

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { accessStateQuery, useAccessState } from '@/hooks/useAccessState';
import { useSession } from '@/hooks/useSession';

export function PastDueBanner() {
  const { user } = useSession();
  const { accessState, loading } = useAccessState();
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const handledReturn = useRef(false);

  const [fromPortal] = useState(() =>
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('from') === 'portal'
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (loading) return;
    if (handledReturn.current) return;
    if (!fromPortal) return;

    handledReturn.current = true;

    setRetrying(true);
    fetch('/api/billing/retry-payment', { method: 'POST' })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (res.status === 402) {
          setError(json.message ?? 'Your card was declined.');
          setRetrying(false);
          return;
        }
        if (!res.ok) {
          setError(json.error ?? `Retry failed (${res.status})`);
          setRetrying(false);
          return;
        }
        if (json.kind === 'paid') {
          // Keep `retrying` true until billingHealth flips so the banner
          // doesn't flash failure copy between the 200 and the webhook.
          window.setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: accessStateQuery(user?.id).queryKey });
          }, 2000);
        } else {
          setRetrying(false);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Retry failed');
        setRetrying(false);
      });

    const params = new URLSearchParams(window.location.search);
    params.delete('from');
    const cleaned = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${cleaned ? `?${cleaned}` : ''}`
    );
  }, [loading, queryClient, user?.id, fromPortal]);

  if (loading) return null;
  if (accessState.billingHealth !== 'failing') return null;

  const handleUpdate = async () => {
    setOpening(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/create-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok || !json.data?.portal_url) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      window.location.href = json.data.portal_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setOpening(false);
    }
  };

  return (
    <div className="flex-shrink-0 border-b border-amber-500/30 dark:border-amber-500/20 bg-amber-500/10 dark:bg-amber-500/[0.08] animate-slide-down">
      <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-500/30 dark:bg-amber-500/[0.12] dark:ring-amber-500/25">
            <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            {retrying ? (
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                Retrying with your updated payment method…
              </p>
            ) : (
              <>
                <p className="text-sm font-semibold tracking-tight text-amber-900 dark:text-amber-100">
                  We couldn&apos;t charge your payment method
                </p>
                <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-300/90">
                  Update your payment method to keep access.
                </p>
              </>
            )}
            {error && (
              <p className="mt-1.5 text-xs font-medium text-red-700 dark:text-red-400">
                {error}
              </p>
            )}
          </div>
        </div>
        {retrying ? (
          <div className="flex items-center gap-2 self-start pl-11 text-xs font-medium text-amber-700 dark:text-amber-300 sm:self-auto sm:pl-0">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Working…</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleUpdate}
            disabled={opening}
            className="inline-flex flex-shrink-0 items-center justify-center gap-1.5 self-start rounded-md bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-amber-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-amber-600/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-amber-50 dark:focus-visible:ring-offset-amber-900/30 sm:self-auto"
          >
            {opening && <Loader2 className="h-3 w-3 animate-spin" />}
            {opening ? 'Opening portal…' : 'Update payment method'}
          </button>
        )}
      </div>
    </div>
  );
}
